import React from 'react';
import { Table } from 'syngenta-digital-cropwise-react-ui-kit';
import 'styles/uiKitTableReset.css';
import './index.css';

// The column shape the Property tables already declared for LandingTable.
export type TableColumn = {
  field: string;
  title: string;
  sortable?: boolean;
  width?: string;
  position?: 'right';
};

export type SortDir = 'asc' | 'desc';

type SortableTableProps<Row> = {
  columns: TableColumn[];
  rows: Row[];
  renderCell: (field: string, row: Row) => React.ReactNode;
  sortBy?: string | null;
  sortDir?: SortDir | null;
  onSort?: (field: string, dir: SortDir | null) => void;
  allowUnsort?: boolean;
  // Pins the first column and clears row hover, for the wide detail tables.
  sticky?: boolean;
  // Minimum table width before it scrolls sideways.
  scrollX?: number;
  // Body height before it scrolls; the header stays in place.
  scrollY?: number;
  'data-testid'?: string;
};

const TO_ANTD = { asc: 'ascend', desc: 'descend' } as const;

// grow-ui's sort glyphs: one icon that follows the state, where antd shows
// two fixed carets.
const SORT_ICON_PATHS = {
  asc:
    'M8.5575 14.7802L12 11.3452L15.4425 14.7802L16.5 13.7227L12 9.22266L7.5 13.7227L8.5575 14.7802Z',
  desc:
    'M8.5575 9.22266L12 12.6577L15.4425 9.22266L16.5 10.2802L12 14.7802L7.5 10.2802L8.5575 9.22266Z',
  none:
    'M14.3776 9.75L12.0001 7.3725L9.62262 9.75L8.55762 8.6925L12.0001 5.25L15.4351 8.6925L14.3776 9.75ZM9.62262 14.25L12.0001 16.6275L14.3776 14.25L15.4426 15.3075L12.0001 18.75L8.56512 15.3075L9.62262 14.25Z'
};

const SortIcon = ({ dir }: { dir: SortDir | null }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    data-testid={`sort-icon-${dir ?? 'none'}`}
    className="cwf-kit-sortable-table__sort-icon"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d={SORT_ICON_PATHS[dir ?? 'none']}
      fill="#696F88"
    />
  </svg>
);

type SortOrder = typeof TO_ANTD[SortDir];

type SorterResult = {
  columnKey?: React.Key;
  order?: SortOrder | null;
};

/**
 * A UI Kit table driven by the LandingTable column definitions, sorted by
 * the caller. Tables may opt into clearing the sort after descending.
 */
const SortableTable = <Row extends object>({
  columns,
  rows,
  renderCell,
  sortBy = null,
  sortDir = null,
  onSort,
  allowUnsort = false,
  sticky = false,
  scrollX,
  scrollY,
  'data-testid': dataTestId
}: SortableTableProps<Row>) => {
  const hasSorting = columns.some(column => column.sortable);

  const tableColumns = columns.map(
    ({ field, title, sortable, width, position }, index) => ({
      key: field,
      title: sortable ? (
        <span
          className={`cwf-kit-sortable-table__title ${
            position === 'right' ? 'justify-end' : ''
          }`}
        >
          {title}
          <SortIcon dir={sortBy === field ? sortDir : null} />
        </span>
      ) : (
        title
      ),
      width,
      align: position === 'right' ? ('right' as const) : undefined,
      fixed: sticky && index === 0 ? ('left' as const) : undefined,
      sorter: sortable ? true : undefined,
      sortDirections: ['ascend', 'descend'] as SortOrder[],
      sortOrder:
        sortable && sortBy === field && sortDir ? TO_ANTD[sortDir] : null,
      render: (_: unknown, row: Row) => renderCell(field, row)
    })
  );

  // Rows carry no stable id of their own; they are keyed by position, which
  // is what they are rendered by.
  const dataSource = rows.map((row, index) => ({ ...row, $rowKey: index }));

  const handleChange = (
    _pagination: unknown,
    _filters: unknown,
    sorter: SorterResult | SorterResult[]
  ) => {
    const { columnKey, order } = Array.isArray(sorter) ? sorter[0] : sorter;
    if (!onSort || (columnKey === undefined && (!allowUnsort || !sortBy))) {
      return;
    }
    const field = String(columnKey ?? sortBy);
    if (allowUnsort) {
      const nextDir = { ascend: 'asc', descend: 'desc' } as const;
      onSort(field, order ? nextDir[order] : null);
      return;
    }
    const toggled = sortDir === 'asc' ? 'desc' : 'asc';
    onSort(field, field === sortBy ? toggled : 'asc');
  };

  // Row hover follows the grow-ui tables: blue on the plain ones, none on
  // the sticky ones (their stylesheet cleared it).
  const classes = [
    'cwf-kit-sortable-table',
    'cwf-table-flush',
    allowUnsort ? 'cwf-kit-sortable-table--allow-unsort' : '',
    hasSorting ? '' : 'cwf-table-static-header',
    sticky ? 'cwf-table-no-row-hover cwf-kit-sortable-table--sticky' : '',
    scrollX ? 'cwf-kit-sortable-table--scroll-x' : ''
  ].join(' ');

  return (
    <div className={classes} data-testid={dataTestId}>
      <Table
        rowKey="$rowKey"
        columns={tableColumns}
        dataSource={dataSource}
        pagination={false}
        onChange={handleChange}
        scroll={scrollX || scrollY ? { x: scrollX, y: scrollY } : undefined}
      />
    </div>
  );
};

export default SortableTable;
